import { Head, Link, router, useForm } from '@inertiajs/react'
import { Filter, MapPin, RefreshCcw, Sparkles, Users } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { Button, buttonClasses } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { EmptyState } from '@/components/ui/EmptyState'
import { Input } from '@/components/ui/Input'
import type { Breadcrumb } from '@/types/shared'

type PlayerSummary = {
  id: string
  name: string
  prefecture?: string | null
  teamName?: string | null
  positions: string[]
  height?: number | null
  dominantFoot?: string | null
  dominantFootLabel?: string | null
  boarding?: boolean | null
  fiftyMeterTime?: number | null
  updatedAt: string
}

type Filters = {
  prefectureIds: number[]
  heightRange?: string | null
  positionIds: number[]
  dominantFoots: string[]
  sprintTime?: string | null
  boarding?: string | null
  keyword?: string | null
}

type FilterOptions = {
  prefectures: Array<{ code: number; name: string }>
  positions: Array<{ id: number; name: string }>
  dominantFoots: Array<{ value: string; label: string }>
  heightRanges: Array<{ value: string | null; label: string }>
  sprintTimes: Array<{ value: string | null; label: string }>
  boardingOptions: Array<{ value: string | null; label: string }>
}

export type SchoolPlayersIndexProps = {
  players: PlayerSummary[]
  matchesCount: number
  filters: Filters
  options: FilterOptions
  breadcrumbs: Breadcrumb[]
}

type FormData = {
  prefecture_ids: string[]
  position_ids: string[]
  dominant_foots: string[]
  height_range: string
  sprint_time: string
  boarding: string
  keyword: string
}

export default function SchoolPlayersIndex({ players, matchesCount, filters, options, breadcrumbs }: SchoolPlayersIndexProps) {
  const form = useForm<FormData>({
    prefecture_ids: filters.prefectureIds.map(String),
    position_ids: filters.positionIds.map(String),
    dominant_foots: filters.dominantFoots.map(String),
    height_range: filters.heightRange ?? '',
    sprint_time: filters.sprintTime ?? '',
    boarding: filters.boarding ?? '',
    keyword: filters.keyword ?? '',
  })

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    form.get('/school/players', {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    })
  }

  const resetFilters = () => {
    router.get('/school/players')
  }

  return (
    <AppShell>
      <Head title="選手検索" />
      <PageHeader
        title="選手検索"
        description="条件を組み合わせて全国の選手を検索し、気になる選手にはすぐにコンタクトできます。"
        breadcrumbs={breadcrumbs}
        actions={
          <Badge variant="outline" className="text-xs text-white/80">
            該当候補 {matchesCount} 名
          </Badge>
        }
      />

      <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,2fr)_minmax(360px,1fr)]">
        <div className="space-y-6">
          <Card padding="lg" className="space-y-4">
            <header className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-white/40">サマリー</p>
                <h2 className="text-lg font-semibold text-white">絞り込み結果</h2>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={resetFilters}>
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  条件をリセット
                </Button>
                <Button asChild size="sm" variant="secondary">
                  <Link href="/school/chatrooms">
                    チャット一覧へ
                    <Users className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </header>
            <p className="text-sm text-white/70">
              条件に合致する {matchesCount} 名の候補が見つかりました。最新の更新順に表示しています。
            </p>
          </Card>

          <Card padding="lg" className="space-y-4">
            <header className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">候補一覧</h2>
              <span className="rounded-full bg-white/10 px-4 py-1 text-xs text-white/70">最新更新順</span>
            </header>

            {players.length === 0 ? (
              <EmptyState
                icon={<Filter className="h-8 w-8" />}
                title="該当する選手が見つかりません"
                description="条件を緩めたり、表示件数を広げて再検索してみましょう。"
                action={
                  <Button size="sm" onClick={resetFilters}>
                    条件をリセット
                  </Button>
                }
              />
            ) : (
              <div className="space-y-4">
                {players.map((player) => (
                  <Link
                    key={player.id}
                    href={`/school/players/${player.id}`}
                    className="flex flex-col gap-4 rounded-3xl border border-white/10 bg-white/5 p-5 transition hover:border-white/20 hover:bg-white/10"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-base font-semibold text-white">{player.name}</p>
                        <p className="text-xs text-white/60">
                          {(player.teamName ?? '所属未登録') + ' · ' + (player.prefecture ?? '所在地未登録')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-white/50">
                        <MapPin className="h-4 w-4" />
                        {formatDate(player.updatedAt)} 更新
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      {player.positions.length ? (
                        player.positions.map((position) => (
                          <span key={position} className="rounded-full bg-brand-500/20 px-3 py-1 text-brand-100">
                            {position}
                          </span>
                        ))
                      ) : (
                        <span className="rounded-full bg-white/10 px-3 py-1 text-white/60">ポジション未設定</span>
                      )}
                      <span className="rounded-full bg-white/10 px-3 py-1 text-white/70">{formatHeight(player.height)}</span>
                      <span className="rounded-full bg-white/10 px-3 py-1 text-white/70">{player.dominantFootLabel || '利き足未設定'}</span>
                      <span className="rounded-full bg-white/10 px-3 py-1 text-white/70">{formatSprint(player.fiftyMeterTime)}</span>
                      {player.boarding != null ? (
                        <Badge variant={player.boarding ? 'success' : 'danger'}>
                          {player.boarding ? '下宿可能' : '下宿不可'}
                        </Badge>
                      ) : (
                        <Badge variant="outline">下宿未登録</Badge>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </div>

        <Card padding="lg" className="space-y-6">
          <form className="space-y-6" onSubmit={submit}>
            <header className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white">検索条件</h2>
                <p className="text-xs text-white/50">複数選択で精度の高い検索が可能です</p>
              </div>
              <Button type="submit" size="sm">
                <Sparkles className="mr-2 h-4 w-4" />
                再検索
              </Button>
            </header>

            <Input
              placeholder="キーワード検索（名前・チーム）"
              value={form.data.keyword}
              onChange={(event) => form.setData('keyword', event.target.value)}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
            />

            <FilterSection title="都道府県" hint="複数選択可">
              <div className="grid max-h-48 grid-cols-2 gap-2 overflow-y-auto pr-2 text-xs md:grid-cols-3">
                {options.prefectures.map((prefecture) => {
                  const value = prefecture.code.toString()
                  const checked = form.data.prefecture_ids.includes(value)
                  return (
                    <label
                      key={prefecture.code}
                      className="flex cursor-pointer items-center gap-2 rounded-xl bg-white/5 px-3 py-2 hover:bg-white/10"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-white/20 bg-transparent"
                        checked={checked}
                        onChange={() => toggleMulti(form, 'prefecture_ids', value)}
                      />
                      <span>{prefecture.name}</span>
                    </label>
                  )
                })}
              </div>
            </FilterSection>

            <FilterSection title="ポジション" hint="複数選択可">
              <div className="flex flex-wrap gap-2 text-xs">
                {options.positions.map((position) => {
                  const value = position.id.toString()
                  const active = form.data.position_ids.includes(value)
                  return (
                    <button
                      type="button"
                      key={position.id}
                      onClick={() => toggleMulti(form, 'position_ids', value)}
                      className={buttonClasses({
                        variant: active ? 'primary' : 'ghost',
                        size: 'xs',
                      })}
                    >
                      {position.name}
                    </button>
                  )
                })}
              </div>
            </FilterSection>

            <FilterSection title="身長">
              <select
                value={form.data.height_range}
                onChange={(event) => form.setData('height_range', event.target.value)}
                className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm text-white focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
              >
                {options.heightRanges.map((option) => (
                  <option key={option.label} value={option.value ?? ''}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FilterSection>

            <FilterSection title="利き足">
              <div className="flex flex-wrap gap-2 text-xs">
                {options.dominantFoots.map((option) => {
                  const active = form.data.dominant_foots.includes(option.value)
                  return (
                    <button
                      type="button"
                      key={option.value}
                      onClick={() => toggleMulti(form, 'dominant_foots', option.value)}
                      className={buttonClasses({
                        variant: active ? 'secondary' : 'ghost',
                        size: 'xs',
                      })}
                    >
                      {option.label}
                    </button>
                  )
                })}
              </div>
            </FilterSection>

            <FilterSection title="50m走">
              <select
                value={form.data.sprint_time}
                onChange={(event) => form.setData('sprint_time', event.target.value)}
                className="w-full rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm text-white focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
              >
                {options.sprintTimes.map((option) => (
                  <option key={option.label} value={option.value ?? ''}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FilterSection>

            <FilterSection title="下宿">
              <div className="grid grid-cols-3 gap-2 text-xs">
                {options.boardingOptions.map((option) => {
                  const value = option.value ?? ''
                  const active = form.data.boarding === value
                  return (
                    <button
                      type="button"
                      key={option.label}
                      onClick={() => form.setData('boarding', value)}
                      className={buttonClasses({
                        variant: active ? 'secondary' : 'ghost',
                        size: 'xs',
                      })}
                    >
                      {option.label}
                    </button>
                  )
                })}
              </div>
            </FilterSection>
          </form>
        </Card>
      </div>
    </AppShell>
  )
}

type MultiSelectField = 'prefecture_ids' | 'position_ids' | 'dominant_foots'
type FormInstance = ReturnType<typeof useForm<FormData>>

function toggleMulti(form: FormInstance, field: MultiSelectField, value: string) {
  const current = new Set(form.data[field])
  if (current.has(value)) {
    current.delete(value)
  } else {
    current.add(value)
  }
  form.setData(field, Array.from(current))
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
  })
}

function formatHeight(value?: number | null) {
  if (value == null) return '身長未登録'
  return `${Number(value)} cm`
}

function formatSprint(value?: number | null) {
  if (value == null) return '50m未登録'
  const numeric = Number(value)
  if (Number.isNaN(numeric)) return '50m未登録'
  return `${numeric.toFixed(1)} 秒`
}

type FilterSectionProps = {
  title: string
  hint?: string
  children: React.ReactNode
}

function FilterSection({ title, hint, children }: FilterSectionProps) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-white">{title}</p>
        {hint ? <span className="text-[11px] text-white/40">{hint}</span> : null}
      </div>
      {children}
    </section>
  )
}
